import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { money } from "@/components/common/RateBreakdown";
import { useAuth } from "@/context/AuthContext";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { api, fmtDate, formatError } from "@/lib/api";
import { RATE_TYPES } from "@/lib/constants";
import { Field } from "@/pages/CompanyFormDialog";
import { AddRateDialog } from "./ContractDialogs";

const todayIso = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Sydney" }).format(new Date());

function CurrentRate({ r }) {
  if (!r) return null;
  const cells = [["Gross", money(r.gross_rate)], [`PayMe fee (${r.payme_fee_percent ?? 0}%)`, money(r.payme_fee)], [`Super (${r.super_guarantee_percent}% SG)`, money(r.super_amount)], ["Taxable rate", money(r.taxable_rate)]];
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="current-rate-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Current rate · {RATE_TYPES[r.rate_type]}</h3>
        <span className="text-xs text-slate-400">since {fmtDate(r.effective_from)}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cells.map(([l, v], i) => (
          <div key={l} className={`rounded-md border p-3 ${i === 3 ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
            <div className="text-[11px] text-slate-500">{l}</div>
            <div className={`font-mono text-lg font-semibold ${i === 3 ? "text-blue-700" : "text-slate-900"}`} data-testid={`current-rate-${i}`}>{v}</div>
          </div>
        ))}
      </div>
      {(r.oncall_rate || r.onshore_rate || r.offshore_rate) && <p className="text-xs text-slate-500 mt-3">On-call {money(r.oncall_rate)} · Onshore {money(r.onshore_rate)} · Offshore {money(r.offshore_rate)}</p>}
    </section>
  );
}

export function RatesTab({ c, reloadKey }) {
  const { canWrite } = useAuth();
  const [rates, setRates] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get(`/contracts/${c.id}/rates`).then((r) => setRates(r.data)), [c.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const today = todayIso();
  const current = rates.find((r) => r.effective_from <= today);
  const toggle = async (r, v) => {
    try { await api.patch(`/contracts/${c.id}/rates/${r.id}`, { implemented_in_payroll: v }); toast.success(v ? "Marked implemented in payroll" : "Marked not implemented"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="rates-tab">
      <CurrentRate r={current} />
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Rate history</h3>
        {canWrite && <Button size="sm" onClick={() => setAdding(true)} data-testid="add-rate-button"><Plus className="h-4 w-4 mr-1" />Add rate change</Button>}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 text-left">{["Effective from", "Gross", "Fee %", "Super", "Taxable", "In payroll", "Changed by"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`rate-row-${r.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.effective_from)}{r.effective_from > today && <span className="ml-2 text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5" data-testid={`rate-scheduled-${r.id}`}>Scheduled</span>}</td>
                <td className="px-4 py-3 font-mono">{money(r.gross_rate)} <span className="text-[11px] text-slate-400">{RATE_TYPES[r.rate_type]?.toLowerCase()}</span></td>
                <td className="px-4 py-3 font-mono">{r.payme_fee_percent ?? "—"}</td>
                <td className="px-4 py-3 font-mono">{money(r.super_amount)} <span className="text-[11px] text-slate-400">@{r.super_guarantee_percent}%</span></td>
                <td className="px-4 py-3 font-mono">{money(r.taxable_rate)}</td>
                <td className="px-4 py-3"><Switch checked={r.implemented_in_payroll} disabled={!canWrite} onCheckedChange={(v) => toggle(r, v)} data-testid={`rate-implemented-${r.id}`} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.updated_by || "—"}</td>
              </tr>
            ))}
            {!rates.length && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">No rates yet</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <AddRateDialog contract={c} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function Lease({ c, onChanged }) {
  const { canWrite } = useAuth();
  const lk = useLookups();
  const [form, setForm] = useState({ finance_company_id: "", monthly_before_tax: "", monthly_after_tax: "", lease_end: "", ...(c.lease || {}) });
  const save = async () => {
    try { await api.put(`/contracts/${c.id}/lease`, { ...form, finance_company_id: form.finance_company_id || null }); toast.success("Lease saved"); onChanged(); } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async () => { await api.delete(`/contracts/${c.id}/lease`); toast.success("Lease removed"); onChanged(); };
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="lease-section">
      <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">Novated vehicle lease</h3>
      <fieldset disabled={!canWrite} className="grid sm:grid-cols-2 gap-4">
        <Field label="Finance company">
          <Select value={form.finance_company_id || ""} onValueChange={(v) => setForm({ ...form, finance_company_id: v })} disabled={!canWrite}>
            <SelectTrigger data-testid="lease-finance-select"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{toOpts(lk?.finance_companies).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Lease end"><Input type="date" value={form.lease_end || ""} onChange={(e) => setForm({ ...form, lease_end: e.target.value })} data-testid="lease-end-input" /></Field>
        <Field label="Monthly before tax (AUD)"><Input className="font-mono" value={form.monthly_before_tax || ""} onChange={(e) => setForm({ ...form, monthly_before_tax: e.target.value })} data-testid="lease-before-input" /></Field>
        <Field label="Monthly after tax (AUD)"><Input className="font-mono" value={form.monthly_after_tax || ""} onChange={(e) => setForm({ ...form, monthly_after_tax: e.target.value })} data-testid="lease-after-input" /></Field>
      </fieldset>
      {canWrite && <div className="flex gap-2 mt-4"><Button size="sm" onClick={save} data-testid="lease-save-button">Save lease</Button>{c.lease && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="lease-remove-button">Remove lease</Button>}</div>}
    </section>
  );
}

export function BankLeaseTab({ c, onChanged }) {
  const { canWrite } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [linked, setLinked] = useState(c.bank_account_ids || []);
  useEffect(() => { if (c.contractor_id) api.get(`/people/${c.contractor_id}/bank-accounts`).then((r) => setAccounts(r.data)); }, [c.contractor_id]);
  useEffect(() => setLinked(c.bank_account_ids || []), [c.bank_account_ids]);
  const toggle = async (id) => {
    const next = linked.includes(id) ? linked.filter((x) => x !== id) : [...linked, id];
    try { await api.put(`/contracts/${c.id}/bank-accounts`, { bank_account_ids: next }); setLinked(next); toast.success("Bank accounts updated"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="bank-lease-tab">
      <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Bank accounts used for this contract</h3>
          <Link to={`/contractors/${c.contractor_id}?tab=bank`} className="text-sm text-blue-600 hover:underline" data-testid="manage-bank-link">Manage accounts</Link>
        </div>
        <ul className="divide-y divide-slate-100">
          {accounts.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2 text-sm">
              <Checkbox checked={linked.includes(a.id)} disabled={!canWrite} onCheckedChange={() => toggle(a.id)} data-testid={`bank-link-${a.id}`} />
              <span className="text-slate-800">{a.account_name}</span>
              <span className="font-mono text-xs text-slate-500">{a.bsb} · {a.account_number}</span>
              <span className="ml-auto font-mono text-xs">{a.split_percent}%</span>
            </li>
          ))}
          {!accounts.length && <li className="py-3 text-sm text-slate-400">The contractor has no bank accounts yet.</li>}
        </ul>
      </section>
      <Lease c={c} onChanged={onChanged} />
    </div>
  );
}
